import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PartyPopper } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import useOnboarding from "@/hooks/useOnboarding";
import useTourEngine from "@/hooks/useTourEngine";
import GoLiveChecklistWidget from "@/components/GoLiveChecklistWidget";

/**
 * Phase B4 — Onboarding host.
 *
 * Mounted globally inside AppShell. Owns the per-user onboarding state
 * fetch, decides when to auto-trigger the Shepherd tour, surfaces the
 * "Restart" event from HelpDropdown via a window event, renders the
 * persistent Go-live checklist widget, AND watches for the 6/6 trial-
 * completion transition to fire the one-shot conversion-nudge toast
 * (Phase B4.2).
 */
const SUPPRESS_WIDGET_PREFIXES = ["/kiosk", "/help", "/super-admin"];

export default function OnboardingHost() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading, refresh, patch, reset, seedSample } = useOnboarding(user);
  const [tourActive, setTourActive] = useState(false);
  const { pathname } = useLocation();
  // Tracks the previous checklist completion so we can fire the
  // conversion nudge precisely on the < total → === total edge — not
  // every render that happens to be 6/6.
  const prevCompletedRef = useRef(null);

  // Auto-trigger logic: org_admin + tour_status === "not_started" → start
  // after a 1500ms grace period the first time we land on a real page.
  useEffect(() => {
    if (!user || loading || !data) return;
    if (user.role !== "org_admin") return;
    const status = data.tour?.tour_status;
    if (status === "not_started") {
      const t = setTimeout(() => setTourActive(true), 1500);
      return () => clearTimeout(t);
    }
    if (status === "in_progress") {
      const t = setTimeout(() => setTourActive(true), 800);
      return () => clearTimeout(t);
    }
  }, [user, loading, data]);

  // Listen for the "Restart onboarding tour" item from HelpDropdown.
  useEffect(() => {
    const onRestart = async () => {
      await reset();
      // The reset cleared `conversion_nudge_shown_at` server-side; clear
      // our local edge-detector too so the nudge can fire again after
      // the next 6/6 transition.
      prevCompletedRef.current = null;
      setTourActive(false);
      setTimeout(() => setTourActive(true), 200);
    };
    window.addEventListener("entrybook:restart-tour", onRestart);
    return () => window.removeEventListener("entrybook:restart-tour", onRestart);
  }, [reset]);

  // "Show go-live checklist" — flip show_checklist_widget=true.
  useEffect(() => {
    const onShow = async () => {
      await patch({ show_checklist_widget: true });
      await refresh();
      toast.success("Checklist restored");
    };
    window.addEventListener("entrybook:show-checklist", onShow);
    return () => window.removeEventListener("entrybook:show-checklist", onShow);
  }, [patch, refresh]);

  // Phase B4.2 — trial conversion nudge.
  //
  // Edge detection: previous < total AND new === total. We DON'T fire on
  // the very first refresh — `prevCompletedRef.current === null` means
  // we haven't observed a "before" state yet, so we just record the
  // current value and bail. This is what stops the nudge from firing
  // for a returning admin who's already at 6/6.
  useEffect(() => {
    if (!data?.checklist) return;
    const cl = data.checklist;
    const tour = data.tour || {};
    const subStatus = data.subscription_status;
    const total = cl.total_count || 0;
    const done = cl.completed_count || 0;
    const prev = prevCompletedRef.current;
    prevCompletedRef.current = done;
    if (prev === null) return;
    if (prev >= total || done < total || total === 0) return;
    if (subStatus !== "trialing") return;
    if (tour.conversion_nudge_shown_at) return;
    if (user?.role !== "org_admin") return;

    // Fire. We optimistically PATCH first so a quick second refresh can
    // see the flag set — if the PATCH fails, we still log the toast
    // (graceful degradation; backend will heal on the next save).
    const nowIso = new Date().toISOString();
    patch({ conversion_nudge_shown_at: nowIso }).catch(() => {});

    toast.success(
      (t) => (
        <div className="flex items-start gap-3" data-testid="trial-conversion-nudge">
          <PartyPopper className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold">You're live — and still on your trial.</div>
            <div className="text-xs mt-0.5 opacity-90">
              Upgrade to keep your kiosks active past day 14.
            </div>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => { toast.dismiss(t); navigate("/billing"); }}
                className="px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold"
                data-testid="trial-conversion-nudge-cta"
              >
                View plans
              </button>
              <button
                type="button"
                onClick={() => toast.dismiss(t)}
                className="px-3 py-1.5 rounded-md text-xs text-navy-700 hover:bg-navy-50"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      ),
      { duration: 12000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.checklist?.completed_count, data?.checklist?.total_count, data?.subscription_status]);

  useTourEngine({
    active: tourActive,
    currentStepId: data?.tour?.current_step_id || null,
    completedStepIds: data?.tour?.completed_step_ids || [],
    seedSampleData: seedSample,
    onTourEnd: async (_status) => {
      setTourActive(false);
      await refresh();
    },
  });

  // Re-fetch the checklist when the route changes so a freshly-created
  // site / host / kiosk flips its row from pending → done without a
  // page reload.
  useEffect(() => {
    if (user && !loading) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const widgetSuppressed = useMemo(
    () => SUPPRESS_WIDGET_PREFIXES.some((p) => pathname.startsWith(p)),
    [pathname],
  );

  const handleDismiss = useCallback(async () => {
    await patch({ show_checklist_widget: false });
    toast.success("Checklist hidden — restore it from Help → Show go-live checklist");
  }, [patch]);

  if (!user || loading || !data) return null;
  const showWidget = data.tour?.show_checklist_widget !== false && !widgetSuppressed;

  if (user.role === "super_admin" && !user.org_id) return null;

  return (
    <>
      {showWidget && (
        <GoLiveChecklistWidget
          checklist={data.checklist}
          onDismiss={handleDismiss}
        />
      )}
    </>
  );
}
